import { LanguageEnum, type BrandCreate, type Country } from '../../api/generated/insights.schemas';
import { canonicalizeCountry, isGlobalCountry } from '../../utils/formOptions';
import type { Extracted, Geography } from './fieldUtils';
import { EMPTY_GEOGRAPHY, ensureUrlScheme, validateWebsiteUrl } from './fieldUtils';
import type { SignedTranscript } from './types';

/**
 * Turn a collected website value into a payload URL (scheme-prefixed), or
 * `undefined` to omit it. The value can come from the assistant's extraction
 * rather than a validated inline edit, so reject anything `validateWebsiteUrl`
 * rejects instead of sending an invalid `https://…` to the API.
 */
const normalizeUrl = (url: string | null | undefined): string | undefined => {
  const trimmed = (url ?? '').trim();
  if (!trimmed || validateWebsiteUrl(trimmed)) return undefined;
  return ensureUrlScheme(trimmed);
};

const cleanString = (value: string | null | undefined): string | undefined => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const cleanList = (value: string[] | null | undefined): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const items = value.map((v) => v.trim()).filter(Boolean);
  return items.length > 0 ? items : undefined;
};

/**
 * Map the brand profile collected during chat onboarding to the payload the
 * create-brand endpoint expects. Each collected value maps onto its own brand
 * field; the backend persists them in dedicated columns and feeds them into
 * query generation. Only populated fields are sent so the rest stay null.
 *
 * When present, the opaque signed transcript is forwarded verbatim: the server
 * verifies it and records how many messages the onboarding chat took (so we can
 * track chat-onboarding efficiency per brand). The client never introspects it.
 */
export const buildBrandCreatePayload = (
  extracted: Extracted,
  signedTranscript?: SignedTranscript | null,
): BrandCreate => {
  const geo = (extracted.target_geography ?? EMPTY_GEOGRAPHY) as Geography;
  // The chat/sidebar store the country as an ISO alpha-2 code or the "Global"
  // worldwide sentinel (empty when the country is unknown). Canonicalise it here
  // ("nl" → "NL", "global" → "Global") — an inline geography edit can leave a
  // lowercase value — so the payload matches the Country enum without relying on
  // the backend to normalise it. Region/city only apply alongside a specific
  // country, so a global brand drops them (guarding against stale leftovers from
  // an earlier country selection).
  const country = canonicalizeCountry(geo.country) ?? undefined;
  const hasSpecificCountry = !!country && !isGlobalCountry(country);

  // The language is picked from the fixed supported-language list by name (or
  // absent); default to English when none was collected. The server re-validates
  // it against the same enum, so a stray value is rejected there rather than here.
  const focusLanguage = (cleanString(extracted.target_language) ??
    'English') as LanguageEnum;

  const payload: BrandCreate = {
    name: cleanString(extracted.brand_name) ?? '',
    url: normalizeUrl(extracted.website_url),
    description: cleanString(extracted.description) ?? '',
    focusLanguage,
    // Canonical ISO code or "Global" — both are members of the generated Country
    // union, so the cast is honest. The backend still re-validates.
    country: country as Country | undefined,
    region: hasSpecificCountry ? cleanString(geo.region) : undefined,
    city: hasSpecificCountry ? cleanString(geo.city) : undefined,
    keyOfferings: cleanList(extracted.key_offerings),
    brandAliases: cleanList(extracted.brand_aliases),
    competitors: cleanList(extracted.competitors),
    targetAudience: cleanString(extracted.target_audience),
    industry: cleanString(extracted.industry),
    uniqueValue: cleanString(extracted.unique_value),
    // Opaque to the client — forwarded verbatim so the server can record how many
    // messages the onboarding took. Same generated type as `BrandCreate`, so no
    // cast is needed.
    signedTranscript: signedTranscript ?? undefined,
  };

  return payload;
};
